<script setup lang="ts">
import { useUserManagementStore } from '@/stores/userManagement';
import { Table, UserAvatar } from '@/components/common';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { type PaginatorData } from '@/components/common/interfaces/PaginatorData';
import { onMounted, ref, watch } from 'vue';
import { UserModel } from '@/models/user';
import Chip from 'primevue/chip';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { StudyUsersService } from '@/services';
import { cloneDeep } from 'lodash';
import UserExpandedNew from './UserExpandedNew.vue';
import type { StudyGroupModel } from '@/models';
import { useStudyGroupsStore } from '@/stores/studyGroups';
import StudyGroupDialog from './groups/StudyGroupDialog.vue';
import UserExpanded from './UserExpanded.vue';

const studyUsersStore = useUserManagementStore();
const groupStore = useStudyGroupsStore();

const userListColumns: TableColumnModel[] = [
  ...studyUsersStore.tableColumns,
  { id: 3, header: 'Groups', key: 'groups', type: TableColumnType.TEXT, sortable: false }
];

const expandedRows = ref<UserModel[]>([]);

const handleRowToggle = (clickedRow: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === clickedRow.identifier
  );

  if (index === -1) {
    expandedRows.value.push(clickedRow);
  } else {
    expandedRows.value.splice(index, 1);
  }
};

const onPerPageRowCountChanged = (perPage: number) => {
  expandedRows.value = [];
  studyUsersStore.onCurrentUsersPerPageChange(perPage);
};

const onPageChange = (pageData: PaginatorData) => {
  expandedRows.value = [];
  studyUsersStore.onCurrentUsersPageChange(pageData);
};

const searchText = ref(studyUsersStore.currentUsersSearch);

watch(
  () => studyUsersStore.currentUsersSearch,
  newSearch => {
    searchText.value = newSearch;
  }
);

const onSearch = () => {
  expandedRows.value = [];
  studyUsersStore.handleCurrentUsersSearch(searchText.value);
};

const onSearchInput = () => {
  if (searchText.value === '') {
    onSearch();
  }
};

onMounted(async () => {
  await studyUsersStore.fetchCurrentUsers();
});

const getUserCombinedPermissions = (user: UserModel)  => {
  const userPermissions = cloneDeep(user.permissions);
  for (const group of user.groups ?? []) {
    if (group.permissions) {
      if (group.permissions.export) {
        userPermissions.export = true;
      }
      if (group.permissions.manageInvest) {
        userPermissions.manageInvest = true;
      }
      if (group.permissions.viewEntries) {
        userPermissions.viewEntries = true;
      }
      if (group.permissions.editCases) {
        userPermissions.editCases = true;
      }
      if (group.permissions.createStudy) {
        userPermissions.createStudy = true;
      }
    }
  }
  const permissionsList = StudyUsersService.parsePermissions(userPermissions);
  const userClone = cloneDeep(user);
  userClone.permissionsList = permissionsList;
  return userClone;
};

const openGroupDialog = ref<boolean>(false);

const groupEditClick = (data: StudyGroupModel) => {
  groupStore.updateGroup(data);
  openCloseGroupDialog();
};

const openCloseGroupDialog = (reloadGroups?: boolean) => {
  openGroupDialog.value = !openGroupDialog.value;
  if (reloadGroups === true) {
    groupStore.getStudyGroups();
  }
  studyUsersStore.currentUsers.forEach(user => {
    const groupIndex = user.groups?.findIndex(g => g.identifier === groupStore.updatedGroup?.identifier);
    if (user.groups && groupIndex !== -1 && groupIndex !== undefined) {
      user.groups[groupIndex] = cloneDeep(groupStore.updatedGroup!);
    }
  });
  studyUsersStore.currentUsers = [...studyUsersStore.currentUsers];
};

const openUserPermissionDialog = ref<boolean>(false);
const selectedUser = ref<UserModel>();

const userPermissionEditClick = (data: UserModel) => {
  selectedUser.value = cloneDeep(data);
  openCloseUserPermissionDialog();
};

const openCloseUserPermissionDialog = () => {
  openUserPermissionDialog.value = !openUserPermissionDialog.value;
};
</script>

<template>
  <div class="user-list-root">
    <div class="user-search">
      <InputText id="user-search"
        v-model="searchText"
        type="text"
        @input="onSearchInput"
        @keyup.enter="onSearch"
        placeholder="Search users"
        aria-label="Search users"
        class="user-search-input"/>
      <Button icon="pi pi-search"
        aria-label="Search"
        type="button"
        outlined
        class="search-button"
        @click="onSearch"
        v-ripple/>
    </div>
    <div class="user-table-wrapper">
      <Table :columns="userListColumns"
    :data-list="studyUsersStore.currentUsers"
    :loading-state="studyUsersStore.isCurrentUsersLoading.isLoading"
    :expandedRows="expandedRows"
    :rows="studyUsersStore.currentUsersLimit"
    :totalRecords="studyUsersStore.totalCurrentUsersCount"
    :currentRow="(studyUsersStore.currentUsersPage - 1) * studyUsersStore.currentUsersLimit"
    @onPageChange="onPageChange"
    @onPerPageRowCountChanged="onPerPageRowCountChanged"
    @onRowClick="handleRowToggle">
    <template #bodyContent="{ data, col }">
      <UserAvatar :avatarUrl="data.avatar"
        :userName="data[col.key]"
        :user-email="data.email"
        :size="44"
        v-if="col.type === TableColumnType.USER"/>

      <div v-if="
          col.key === 'permissionsList' &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        "
        class="permissions-container">
        <span v-for="(permission, index) in getUserCombinedPermissions(data).permissionsList"
          :key="index">
          <Chip :label="permission" />
        </span>
      </div>

      <div v-else-if="
          col.key === 'groups' &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        "
        class="permissions-container">
        <span v-for="group in data.groups" :key="group.identifier">
          <Chip :label="group.name" />
        </span>
        </div>

      <div v-else-if="
          col.type === TableColumnType.TEXT &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        ">
        {{ data[col.key] }}
      </div>
    </template>
    <template #expandContent="{ data }">
      <UserExpandedNew :user="data"
        @onGroupEdit="groupEditClick"
        @onUserEdit="userPermissionEditClick"/>
    </template>
      </Table>
    </div>
  </div>
  <StudyGroupDialog :visible="openGroupDialog"
    v-if="openGroupDialog"
    @onClose="openCloseGroupDialog"/>
  <UserExpanded :visible="openUserPermissionDialog"
    v-if="openUserPermissionDialog && selectedUser"
    :user="selectedUser"
    @onClose="openCloseUserPermissionDialog"/>
</template>

<style scoped lang="scss">
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}

.permissions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.user-list-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.user-search {
  display: flex;
  flex-shrink: 0;
  margin-bottom: 16px;
}
.user-table-wrapper {
  flex: 1;
  min-height: 0;
}
.user-search-input {
  font-size: 14px;
  min-width: 250px;
}
.search-button {
  margin-left: 12px;
}
</style>
